///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Tippy from "@tippyjs/react";
import { isEqual } from "lodash";

import FilterIcon from "../icons/FilterIcon";
import AutoComplete from "../form-controls/AutoComplete";
import {
  filterActiveBids,
  resetFilters,
  initialState
} from "../../slices/activeBidsSlice";
import messages from "../../helpers/messages";
import { userRole } from "../../helpers/types";
import ClearFilterIcon from "../icons/ClearFilterIcon";

const Menu = ({ cardView, user, filtersApplied }) => {
  const { filters } = useSelector(state => state.activeBids);
  const dispatch = useDispatch();

  const handleChange = (field, value) => {
    dispatch(filterActiveBids({ field, value, user, cardView }));
  };

  const CheckBox = ({ field, label, className }) => (
    <label className={"filter-control " + (className || "")}>
      <input
        type="checkbox"
        checked={filters[field]}
        onChange={({ target }) => handleChange(field, target.checked)}
        data-testid={field}
      />
      <span>{label}</span>
    </label>
  );

  const Radio = ({ label, className, field, value }) => (
    <label className={"filter-control " + (className || "")}>
      <input
        type="radio"
        checked={value}
        onChange={() => handleChange(field, !filters[field])}
        radioGroup="sort-by"
      />
      <span>{label}</span>
    </label>
  );

  return (
    <div className="filter-popper card">
      <button
        onClick={() => dispatch(resetFilters({ user }))}
        className="clear-filter"
        disabled={!filtersApplied}
      >
        <ClearFilterIcon
          fill={filtersApplied ? "#343434" : "#ADAFB2"}
          height="16px"
          width="16px"
        />
      </button>
      {cardView && (
        <>
          <div className="label">{messages.sortBy.defaultMessage}</div>
          <Radio
            label={messages.pricingDueDateWithoutDate.defaultMessage}
            value={filters.sortByRfqCloseDate}
            field="sortByRfqCloseDate"
          />
          <Radio
            label={messages.aToZ.defaultMessage}
            className="mb-16"
            value={!filters.sortByRfqCloseDate}
            field="sortByRfqCloseDate"
          />
        </>
      )}

      <div className="label">{messages.showMe.defaultMessage}</div>
      <CheckBox label={messages.bidsIOwn.defaultMessage} field="bidsIOwn" />
      <CheckBox
        label={messages.bidsIAmInTheTeam.defaultMessage}
        field="bidsIAmInTheTeam"
      />
      {(user.role === userRole.tlPricingAnalyst ||
        user.role === userRole.assetBasedPricingUser ||
        user.userRoleForManagementUser === userRole.tlPricingAnalyst ||
        user.userRoleForManagementUser === userRole.assetBasedPricingUser) && (
        <CheckBox
          label={messages.bidsIHaveAssignments.defaultMessage}
          field="bidsIHaveAssignments"
        />
      )}

      <div className="label mt-16 mb-8">{messages.users.defaultMessage}</div>
      <div data-testid="users-container">
        <AutoComplete
          label={messages.name.defaultMessage}
          staticOptions={filters.users}
          isMulti={true}
          value={filters.selectedUsers}
          handleChange={value => handleChange("selectedUsers", value)}
          isSearchable={true}
        />
      </div>

      <div className="label mb-8">{messages.tags.defaultMessage}</div>
      <div data-testid="tags-container">
        <AutoComplete
          label={messages.tags.defaultMessage}
          isMulti={true}
          handleChange={value => handleChange("selectedTags", value)}
          value={filters.selectedTags}
          staticOptions={filters.tags}
        />
      </div>

      {!cardView && (
        <>
          <div data-testid="bid-status" className="label mb-8">
            {messages.bidStatus.defaultMessage}
          </div>
          <div data-testid="bid-status-container">
            <AutoComplete
              data-testid="bid-status-dropdown"
              isMulti={true}
              handleChange={value => handleChange("selectedBidStatus", value)}
              value={filters.selectedBidStatus}
              label={messages.status.defaultMessage}
              staticOptions={filters.bidStatus}
            />
          </div>
        </>
      )}

      <div className="label mb-8">{messages.bidType.defaultMessage}</div>
      <div data-testid="bid-type-container">
        <AutoComplete
          label={messages.bidType.defaultMessage}
          isMulti={true}
          handleChange={value => handleChange("selectedBidTypes", value)}
          value={filters.selectedBidTypes}
          staticOptions={filters.bidTypes}
        />
      </div>
    </div>
  );
};

export const Filter = ({ cardView = true, user }) => {
  const [filter, toggleFilter] = useState(false);
  const { isFetching, filters } = useSelector(state => state.activeBids);

  const filtersApplied = !isEqual(initialState.filters, {
    ...filters,
    users: [],
    tags: [],
    bidStatus: []
  });

  return (
    <Tippy
      appendTo="parent"
      content={
        <Menu cardView={cardView} user={user} filtersApplied={filtersApplied} />
      }
      placement="bottom-start"
      interactive={true}
      visible={filter}
      onClickOutside={() => toggleFilter(false)}
      offset={[0, 3]}
      delay={0}
    >
      <button
        className="filter-menu inline-block mr-12"
        data-testid="filter-menu"
        disabled={isFetching}
        onClick={() => toggleFilter(filter === false)}
      >
        <FilterIcon height="16px" width="16px" />
      </button>
    </Tippy>
  );
};

export default Filter;
